import React from 'react';
import PropTypes from 'prop-types';
import {
    Box, Divider, InputAdornment, Stack, Switch, TextField,
    ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import ConfigCard from './ConfigCard';
import { PdiRow } from './_PdiHelpers';
import { useDesignVariant } from '../../_designVariant';

// Visual position-picker tiles. Each value is the storefront-side identifier
// the gallery component reads to lay out the thumbnails. "hidden" turns the
// thumbnail strip off entirely.
const POSITIONS = [
    { value: 'left',   i18nKey: 'cfg_gallery_pos_left',   fallback: 'Left'   },
    { value: 'right',  i18nKey: 'cfg_gallery_pos_right',  fallback: 'Right'  },
    { value: 'above',  i18nKey: 'cfg_gallery_pos_above',  fallback: 'Above'  },
    { value: 'below',  i18nKey: 'cfg_gallery_pos_below',  fallback: 'Below'  },
    { value: 'hidden', i18nKey: 'cfg_gallery_pos_hidden', fallback: 'Hidden' },
];

// Tiny pictogram per position so admins immediately see what they're picking.
// Pure CSS — no extra icon imports — keeps the bundle lean.
const PositionTile = ({ value }) => {
    const main  = { width: 22, height: 22, backgroundColor: 'currentColor', borderRadius: 2, opacity: 0.9 };
    const thumb = { width: 4,  height: 4,  backgroundColor: 'currentColor', borderRadius: 0.5, opacity: 0.6 };
    if (value === 'hidden') return <Box sx={main} />;
    if (value === 'above' || value === 'below') {
        const thumbs = (
            <Box sx={{ display: 'flex', gap: 0.4 }}>
                {[0,1,2].map(i => <Box key={i} sx={{ ...thumb, width: 6, height: 4 }} />)}
            </Box>
        );
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, alignItems: 'center' }}>
                {value === 'above' && thumbs}
                <Box sx={main} />
                {value === 'below' && thumbs}
            </Box>
        );
    }
    // left / right
    const thumbsCol = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
            {[0,1,2].map(i => <Box key={i} sx={{ ...thumb, width: 4, height: 6 }} />)}
        </Box>
    );
    return (
        <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center' }}>
            {value === 'left' && thumbsCol}
            <Box sx={main} />
            {value === 'right' && thumbsCol}
        </Box>
    );
};

/**
 * "Image gallery" card — controls how product images are laid out on the
 * detail page. Thumbnail position tiles, grid-vs-scroll mode, max thumbnails,
 * and arrow toggles with their per-element colours.
 *
 * value shape:
 *   { thumbnailPosition: 'left' | 'right' | 'above' | 'below' | 'hidden',
 *     useGrid:        bool,
 *     maxThumbnails:  number,
 *     showArrows:     bool,
 *     arrowColor:     string,
 *     arrowBgColor:   string,
 *     showFullscreen: bool,
 *     fullscreenArrowColor:   string,
 *     fullscreenArrowBgColor: string }
 */
const ImageGalleryCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const get = (k, fb) => (value[k] !== undefined ? value[k] : fb);
    const set = (k) => (v) => onChange && onChange(k, v);

    const useGrid       = get('useGrid', true);
    const showArrows    = get('showArrows', true);
    const showFullscreen = get('showFullscreen', true);
    const { tokens } = useDesignVariant();

    // classic keeps a rule between the four topics; doorloop draws its own
    // hairline under every row
    const divider = tokens.row === 'stack' ? <Divider /> : null;
    const switchCtl = (key, checked) => (
        <Switch color="success" size="small" checked={!!checked} onChange={(e) => set(key)(e.target.checked)} />
    );

    return (
        <ConfigCard
            anchor="cfg-card-image-gallery"
            title={tr('cfg_gallery_title', 'Image gallery')}
            description={tr('cfg_gallery_desc', 'How product images appear on the detail page.')}
            theme={theme}
        >
            {/* Thumbnail position — visual tile picker, too wide for the control column */}
            <PdiRow
                id="cfg-topic-gallery-position"
                railTopic={tr('cfg_gallery_topic_position', 'Thumbnails')}
                stacked
                label={tr('cfg_gallery_pos_title', 'Thumbnail position')}
                help={tr('cfg_gallery_pos_desc', 'Where the small image thumbnails appear, relative to the main product image.')}
                control={(
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={get('thumbnailPosition', 'left')}
                        onChange={(_e, v) => v && set('thumbnailPosition')(v)}
                        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, '& .MuiToggleButton-root': { borderRadius: '8px !important', border: '1px solid rgba(0,0,0,0.12) !important' } }}
                    >
                        {POSITIONS.map((p) => (
                            <ToggleButton key={p.value} value={p.value} sx={{ flexDirection: 'column', gap: 0.5, px: 1.5, py: 1, minWidth: 72, textTransform: 'none' }}>
                                <PositionTile value={p.value} />
                                <Typography variant="caption">{tr(p.i18nKey, p.fallback)}</Typography>
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                )}
            />

            {divider}

            {/* Grid vs scroll */}
            <PdiRow
                id="cfg-topic-gallery-grid"
                railTopic={tr('cfg_gallery_topic_grid', 'Grid')}
                label={tr('cfg_gallery_use_grid', 'Use a grid-based gallery')}
                help={tr('cfg_gallery_use_grid_desc', 'Shows a fixed number of thumbnails in a grid and supports playing videos. Turn off for a simple scrollable list.')}
                control={switchCtl('useGrid', useGrid)}
            />
            {useGrid && (
                <PdiRow
                    indent
                    label={tr('cfg_gallery_max_thumbs', 'Maximum thumbnails to show')}
                    help={tr('cfg_gallery_max_thumbs_helper', 'If a product has more images than this, the rest are tucked away behind a "view more" action.')}
                    control={(
                        <TextField
                            type="number"
                            size="small"
                            value={get('maxThumbnails', 3) ?? 3}
                            onChange={(e) => set('maxThumbnails')(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                            InputProps={{ endAdornment: <InputAdornment position="end">{tr('cfg_gallery_thumb_unit', 'thumbnails')}</InputAdornment> }}
                            inputProps={{ min: 1, max: 20, 'aria-label': tr('cfg_gallery_max_thumbs', 'Maximum thumbnails to show') }}
                            sx={{ width: 200 }}
                        />
                    )}
                />
            )}

            {divider}

            {/* Arrows on the main image */}
            <PdiRow
                id="cfg-topic-gallery-arrows"
                railTopic={tr('cfg_gallery_topic_arrows', 'Arrows')}
                label={tr('cfg_gallery_arrows', 'Show arrows on the main image')}
                help={tr('cfg_gallery_arrows_desc', 'Lets visitors flip through images by clicking left/right arrows on the main image.')}
                control={switchCtl('showArrows', showArrows)}
            />
            {showArrows && (
                <PdiRow
                    indent
                    label={tr('cfg_gallery_colors', 'Colours')}
                    control={(
                        <Stack direction="row" spacing={2}>
                            <ColorMini label={tr('cfg_gallery_arrow_color', 'Arrow color')} value={get('arrowColor', '#000000')} onChange={set('arrowColor')} />
                            <ColorMini label={tr('cfg_gallery_arrow_bg', 'Background color')} value={get('arrowBgColor', '#ffffff')} onChange={set('arrowBgColor')} />
                        </Stack>
                    )}
                />
            )}

            {divider}

            {/* Fullscreen arrow */}
            <PdiRow
                id="cfg-topic-gallery-fullscreen"
                railTopic={tr('cfg_gallery_topic_fullscreen', 'Fullscreen')}
                label={tr('cfg_gallery_fullscreen', "Show 'open fullscreen' arrow below the thumbnails")}
                help={tr('cfg_gallery_fullscreen_desc', 'Adds an arrow below the thumbnails that opens a fullscreen preview of the current image.')}
                control={switchCtl('showFullscreen', showFullscreen)}
            />
            {showFullscreen && (
                <PdiRow
                    indent
                    label={tr('cfg_gallery_colors', 'Colours')}
                    control={(
                        <Stack direction="row" spacing={2}>
                            <ColorMini label={tr('cfg_gallery_arrow_color', 'Arrow color')} value={get('fullscreenArrowColor', '#000000')} onChange={set('fullscreenArrowColor')} />
                            <ColorMini label={tr('cfg_gallery_arrow_bg', 'Background color')} value={get('fullscreenArrowBgColor', '#ffffff')} onChange={set('fullscreenArrowBgColor')} />
                        </Stack>
                    )}
                />
            )}
        </ConfigCard>
    );
};

// Compact colour-swatch input — small enough to sit inline next to the toggle
// labels without crowding the card. Uses native `<input type="color">` like
// the rest of the style tab.
const ColorMini = ({ label, value, onChange }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
            component="label"
            sx={{
                position: 'relative', width: 28, height: 28, borderRadius: 1,
                border: '1px solid rgba(0,0,0,0.18)', backgroundColor: value,
                cursor: 'pointer', flexShrink: 0,
            }}
        >
            <input
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
            />
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
    </Box>
);

ImageGalleryCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default ImageGalleryCard;
